import ImageElementDragTarget from './targets/ImageElementDragTarget';
import LyricsDragTarget from './targets/LyricsDragTarget';
import TextElementDragTarget from './targets/TextElementDragTarget';
import { createVisualizerDragTarget } from './targets/VisualizerDragTarget';

// Everything on the canvas a pointer can pick up, in the order it is drawn: the visualizer at the
// back, then the text lines, the lyrics, then the pictures in front of them. The drag controller
// searches the list backwards, so the element a click lands on is the one the user can actually see
// there.
//
// Rebuilt on every pointer event rather than held: a picture can be added, a line of text emptied or
// the visualizer's whole shape swapped between one event and the next, and a list kept across that
// would hand the drag an element the renderer has already destroyed.
export default function collectDragTargets(pipeline, videoSettings, commit) {
    if (!pipeline)
        return [];

    return [
        ...collectVisualizerTargets(pipeline, videoSettings, commit.visualizer),
        ...collectTextTargets(pipeline, videoSettings, commit.text),
        ...collectLyricsTargets(pipeline, commit.lyrics),
        ...collectImageTargets(pipeline, commit.image)
    ];
}

function collectVisualizerTargets(pipeline, videoSettings, commit) {
    const visualizer = pipeline.getVisualizerRenderer();

    // The renderer exists from the moment the pipeline is built, but carries no settings until the
    // first update, and a position is the one thing a drag cannot do without.
    if (!visualizer?.config?.position)
        return [];

    return [createVisualizerDragTarget(visualizer, videoSettings.visualizer.shape, commit)];
}

// The container keeps a fixed pool of text elements and shows as many as the settings ask for, so the
// pool is walked only as far as the settings reach: past that the elements are hidden and their
// leftover settings belong to a line the user has since removed.
//
// A line the user has emptied is skipped as well. PIXI still reports a box for it, because the style
// carries padding for the glow and the shadow, so an empty line would be a rectangle of nothing that
// takes a grab and offers alignment lines to everything else, both of them where the user can see no
// line at all.
function collectTextTargets(pipeline, videoSettings, commit) {
    const settings = videoSettings.text.textElements || [];

    return pipeline.getTextElements()
        .slice(0, settings.length)
        .map((textElement, index) => ({ textElement, index }))
        .filter(({ textElement }) => textElement.config?.position && hasVisibleText(textElement))
        .map(({ textElement, index }) => new TextElementDragTarget(textElement, index, commit));
}

function hasVisibleText(textElement) {
    return typeof textElement.config.text === 'string' && textElement.config.text.trim().length > 0;
}

// The lyrics are one element that shows whichever line is playing, so it is offered only while a line
// is on screen: between two lines, or before the first, there is nothing there to grab or to align by.
// The line is read off the text element itself rather than its settings, which carry no text at all;
// the renderer writes the words in as the playback time reaches them.
function collectLyricsTargets(pipeline, commit) {
    const lyrics = pipeline.getLyricsRenderer();

    if (!lyrics?.textElement?.config?.position || !hasVisibleLyricsLine(lyrics.textElement))
        return [];

    return [new LyricsDragTarget(lyrics, commit)];
}

function hasVisibleLyricsLine(textElement) {
    const line = textElement.text.text;

    return textElement.elementContainer.visible && typeof line === 'string' && line.trim().length > 0;
}

// Ordered bottom-up like the wave layers, so the array index doubles as the stacking order and the
// topmost list item is the one in front.
function collectImageTargets(pipeline, commit) {
    return pipeline.getImageElements()
        .filter(imageElement => imageElement.config?.position)
        .map(imageElement => new ImageElementDragTarget(imageElement, commit));
}
